import { Button } from "@/components/ui/button";
import { Check, Loader2, Send } from "lucide-react";
import { type FormEvent, type KeyboardEvent, useEffect, useRef } from "react";
import { MAX_MESSAGE_LENGTH } from "./message-utils";

export type ComposerStatus = "idle" | "pending" | "completed" | "failed";

type AgentComposerProps = {
  value: string;
  status: ComposerStatus;
  announcement: string;
  errorMessage?: string | null;
  disabled?: boolean;
  disabledReason?: string;
  onChange: (value: string) => void;
  onSubmit: (text: string) => void;
};

export function AgentComposer({
  value,
  status,
  announcement,
  errorMessage,
  disabled = false,
  disabledReason,
  onChange,
  onSubmit,
}: AgentComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previousStatusRef = useRef<ComposerStatus>(status);
  const trimmedValue = value.trim();
  const isPending = status === "pending";
  const isInvalid =
    trimmedValue.length === 0 || trimmedValue.length > MAX_MESSAGE_LENGTH;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!disabled && !isPending && !isInvalid) {
      onSubmit(trimmedValue);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  };

  const buttonLabel =
    status === "pending"
      ? "Sending message"
      : status === "completed"
        ? "Message sent"
        : "Send message";

  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = status;

    if (
      previousStatus === "pending" &&
      (status === "completed" || status === "failed")
    ) {
      textareaRef.current?.focus({ preventScroll: true });
    }
  }, [status]);

  return (
    <form
      aria-busy={isPending}
      className="fixed inset-x-0 bottom-0 z-30 mt-0 border-slate-200 border-t bg-white/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur md:sticky md:inset-x-auto md:z-10 md:mt-6 md:p-4"
      data-testid="agent-composer"
      onSubmit={submit}
    >
      <label className="sr-only" htmlFor="agent-message">
        Message the agent
      </label>

      <div className="mx-auto flex max-w-7xl items-end gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm focus-within:ring-[3px] focus-within:ring-ring/50">
        <textarea
          aria-describedby={[
            "agent-message-help",
            "agent-message-count",
            errorMessage ? "agent-message-error" : null,
          ]
            .filter(Boolean)
            .join(" ")}
          aria-invalid={Boolean(errorMessage)}
          className="max-h-48 min-h-12 min-w-0 flex-1 resize-none bg-transparent px-1 py-2 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-60"
          disabled={disabled || isPending}
          id="agent-message"
          maxLength={MAX_MESSAGE_LENGTH}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            disabled
              ? "This session has ended."
              : "Ask the sales team to research or continue a campaign…"
          }
          ref={textareaRef}
          rows={1}
          value={value}
        />

        <Button
          aria-label={buttonLabel}
          disabled={disabled || isPending || isInvalid}
          size="icon"
          type="submit"
        >
          {status === "pending" ? (
            <Loader2 aria-hidden="true" className="animate-spin" />
          ) : status === "completed" ? (
            <Check aria-hidden="true" />
          ) : (
            <Send aria-hidden="true" />
          )}
        </Button>
      </div>

      <div className="mx-auto mt-2 flex max-w-7xl flex-wrap justify-between gap-2 text-slate-500 text-xs">
        <span id="agent-message-help">
          Enter to send · Shift+Enter for a new line
        </span>
        <span id="agent-message-count">
          {value.length}/{MAX_MESSAGE_LENGTH}
        </span>
      </div>

      {errorMessage ? (
        <p
          className="mx-auto mt-2 max-w-7xl text-destructive text-sm"
          id="agent-message-error"
          role="alert"
        >
          {errorMessage}
        </p>
      ) : null}

      {disabled && disabledReason ? (
        <p className="mx-auto mt-2 max-w-7xl text-slate-600 text-sm">
          {disabledReason}
        </p>
      ) : null}

      <output aria-live="polite" className="sr-only">
        {announcement}
      </output>
    </form>
  );
}
